import { Button, Checkbox, DatePicker, Form, Input, Select, Table, message } from 'antd';
import { useEffect, useState } from 'react';
import { EmptyState } from '../../components/EmptyState';
import { StatusBadge } from '../../components/StatusBadge';
import { newClientToken } from '../../lib/client-token';
import { ApiError } from '../../lib/http';
import { MISSING_REASON } from '../../lib/status';
import { labsApi, periodsApi, requestsApi } from '../../services/resources';
import type { ExperimentRequest, Lab, Period } from '../../types/api';

export function StudentRequestNewPage() {
  const [agreed, setAgreed] = useState(false);
  const [labs, setLabs] = useState<Lab[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [mine, setMine] = useState<ExperimentRequest[]>([]);
  const [form] = Form.useForm();

  async function load() {
    const [l, p, r] = await Promise.all([
      labsApi.list({ page: 1, limit: 50 }),
      periodsApi.list(),
      requestsApi.list({ page: 1, limit: 50 }),
    ]);
    setLabs(l.items);
    setPeriods(p);
    setMine(r.items);
  }

  useEffect(() => {
    void load().catch(() => message.error('加载失败'));
  }, []);

  return (
    <div className="stack">
      <h1 className="page-title">自主实验申请</h1>
      <Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)}>
        已阅读安全须知，服从现场管理
      </Checkbox>
      <Form
        form={form}
        layout="vertical"
        onFinish={async (v) => {
          try {
            await requestsApi.create({
              lab_id: v.lab_id,
              date: v.date.format('YYYY-MM-DD'),
              period_ids: v.period_ids,
              title: v.title,
              description: `指导教师 ${v.advisor} 器材 ${v.tools ?? ''}\n${v.description ?? ''}`,
              client_token: newClientToken(),
            });
            form.resetFields();
            await load();
          } catch (e) {
            message.error(e instanceof ApiError ? e.message : '提交失败');
          }
        }}
      >
        <Form.Item name="title" label="课题" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="advisor" label="指导教师" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="lab_id" label="实验室" rules={[{ required: true }]}>
          <Select options={labs.map((l) => ({ value: l.id, label: `${l.room_code} ${l.name}` }))} />
        </Form.Item>
        <Form.Item name="date" label="日期" rules={[{ required: true }]}>
          <DatePicker />
        </Form.Item>
        <Form.Item name="period_ids" label="节次" rules={[{ required: true }]}>
          <Select mode="multiple" options={periods.map((p) => ({ value: p.id, label: p.name }))} />
        </Form.Item>
        <Form.Item name="tools" label="器材">
          <Input />
        </Form.Item>
        <Button type="primary" htmlType="submit" className="touch" disabled={!agreed}>
          提交申请
        </Button>
      </Form>
      <h2 className="page-title">进度</h2>
      {mine.length === 0 ? (
        <EmptyState text="还没有申请。" />
      ) : (
        <Table
          scroll={{ x: 720 }}
          rowKey="id"
          dataSource={mine}
          pagination={false}
          columns={[
            { title: '课题', dataIndex: 'title' },
            {
              title: '状态',
              render: (_, r) => <StatusBadge status={r.status} reasonType={r.reason_type} />,
            },
            {
              title: '说明',
              render: (_, r) => {
                if (r.sla_overdue && r.status === 'pending') {
                  return '已超过 2 工作日，实验员仍会处理';
                }
                if (r.status === 'rejected') return r.reject_reason || MISSING_REASON;
                return r.due_at;
              },
            },
            {
              title: '操作',
              render: (_, r) =>
                r.status === 'pending' ? (
                  <Button
                    className="touch"
                    onClick={async () => {
                      await requestsApi.withdraw(r.id);
                      await load();
                    }}
                  >
                    撤回
                  </Button>
                ) : null,
            },
          ]}
        />
      )}
    </div>
  );
}
